import { isGuaranteedOverride, type GuaranteedServiceOrgSkuEntry, type PatchGuaranteedServiceRequest } from '@nadoutong/contracts';
import { Trash2 } from 'lucide-react';
import { useT } from '../../i18n/index.ts';
import { IconButton, Select, Table, TBody, Td, Th, THead, Tr } from '../../ui/index.ts';
import { MODE_VALUES, modeLabelKey, overlayFallback } from './modes.ts';

function countOf(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

export function OrgOverlayTable({
  rows,
  customerName,
  customerEmail,
  onPatch,
  onEditNote,
  onDelete,
}: {
  readonly rows: readonly GuaranteedServiceOrgSkuEntry[];
  readonly customerName: (orgId: string) => string;
  readonly customerEmail: (orgId: string) => string;
  readonly onPatch: (id: string, body: PatchGuaranteedServiceRequest) => void;
  readonly onEditNote: (id: string, note: string) => void;
  readonly onDelete: (id: string) => void;
}): JSX.Element {
  const t = useT();
  const options = [
    { value: 'inherit', label: t('guaranteed.inherit') },
    ...MODE_VALUES.map((value) => ({ value, label: t(modeLabelKey(value)) })),
  ];
  return (
    <Table className="rounded-none border-0 bg-transparent" testId="guaranteed-overlay-table">
      <THead>
        <Tr interactive={false}>
          <Th>{t('guaranteed.customer')}</Th>
          <Th>{t('guaranteed.sku')}</Th>
          <Th>{t('guaranteed.overlay.fallback')}</Th>
          <Th num>{t('guaranteed.overlay.retries')}</Th>
          <Th>{t('guaranteed.overlay.note')}</Th>
          <Th>{t('guaranteed.delete')}</Th>
        </Tr>
      </THead>
      <TBody>
        {rows.map((row) => {
          const fallback = overlayFallback(row);
          const email = customerEmail(row.org_id);
          const sub = email.length > 0 ? `${row.org_id} · ${email}` : row.org_id;
          return (
            <Tr key={row.id} data-testid={`guaranteed-overlay-${row.id}`}>
              <Td>
                <span className="block truncate font-medium text-paper" title={customerName(row.org_id)}>
                  {customerName(row.org_id)}
                </span>
                <span className="block truncate font-mono text-micro text-paper-low" title={sub}>{sub}</span>
              </Td>
              <Td>
                <span className="block truncate" title={row.sku_name}>{row.sku_name}</span>
              </Td>
              <Td>
                <Select
                  value={fallback}
                  onValueChange={(value) => {
                    if (!isGuaranteedOverride(value) || value === 'on' || value === fallback) return;
                    onPatch(row.id, { ultimate_fallback: value });
                  }}
                  options={options}
                  ariaLabel={t('guaranteed.overlay.fallback')}
                  testId={`guaranteed-fallback-${row.id}`}
                />
              </Td>
              <Td num>
                <span data-testid={`guaranteed-overlay-24h-${row.id}`}>
                  <span>{countOf(row.entered_chain_count_24h)}</span>
                  {' / '}
                  <span>{countOf(row.request_count_24h)}</span>
                  {' '}
                  {t('guaranteed.overlay.requests')}
                </span>
              </Td>
              <Td>
                <button
                  type="button"
                  className="block max-w-full truncate text-left text-label text-paper-mid"
                  title={row.note ?? ''}
                  data-testid={`guaranteed-note-${row.id}`}
                  onClick={() => onEditNote(row.id, row.note ?? '')}
                >
                  {row.note ?? t('guaranteed.note_placeholder')}
                </button>
              </Td>
              <Td>
                <IconButton
                  label={t('guaranteed.delete')}
                  size="sm"
                  className="hover:text-bad"
                  data-testid={`guaranteed-delete-${row.id}`}
                  onClick={() => onDelete(row.id)}
                >
                  <Trash2 aria-hidden="true" className="h-3.5 w-3.5" />
                </IconButton>
              </Td>
            </Tr>
          );
        })}
      </TBody>
    </Table>
  );
}
